import axios from 'axios'
import Cookies from 'js-cookie'
import store, { isSpinnerSuppressed } from '../store/index'
import { toServiceDomain } from './serviceDomain'

// 동시 401 다발 처리: refresh 진행 중엔 모든 요청을 큐에 모았다가 1회 refresh 후 일괄 재시도
let isRefreshing = false
let refreshSubscribers = []

function onRefreshDone (access) {
    const subs = refreshSubscribers
    refreshSubscribers = []
    subs.forEach(cb => cb(access)) // access=null 이면 실패 통지
}

let lastRefreshTs = 0 // 마지막 refresh 성공 시각 (기기 시계 오차로 인한 연속 refresh 방지)

// refresh 가 서버에서 거부(만료/무효)되면 "이 탭만" 정리하고 메인으로.
// quiet: logoutTime 을 기록하지 않음 — 한 탭의 국소 오류(좀비 탭 stale refresh, 순단 등)가
// 활동 중인 다른 탭까지 로그아웃시키는 증폭 방지. 전 탭 로그아웃은 명시적 로그아웃(delLogin)만 수행.
function forceLogout () {
    store.dispatch('auth/delLoginInfo', { quiet: true }).then(() => {
        location.replace('/')
    })
}

function performRefresh () {
    const refreshToken = sessionStorage.getItem('refresh_token')
    if (!refreshToken) {
        return Promise.reject(new Error('no refresh token'))
    }

    // auth.client 를 import 하면 순환 참조가 되므로, auth 서버로 직접 호출
    let refreshUrl = process.env.VUE_APP_AUTH_URL + '/refresh'
    if (location.host.includes('preview')) {
        refreshUrl = refreshUrl.replace('api.', 'api-preview.')
    }
    refreshUrl = toServiceDomain(refreshUrl)

    return axios.post(refreshUrl, { refresh: refreshToken })
        .then(res => {
            if (res.headers.respcode !== 'C0000') {
                forceLogout() // 서버가 refresh 거부(INVALID_TOKEN 등) = 세션 종료 → 만료 refresh 반복 시도 방지
                throw new Error('refresh rejected')
            }
            const access = res.data.jwt
            sessionStorage.setItem('access_token', access)
            Cookies.set('access_token', access, { path: '/' }) // saveLogin 과 동일하게 쿠키도 갱신
            if (res.data.refreshToken) {
                sessionStorage.setItem('refresh_token', res.data.refreshToken)
            }
            store.commit('auth/jwt', access)
            lastRefreshTs = Date.now()

            // 회전된 토큰을 다른 탭에도 전파 (다른 탭이 옛 refresh 로 재시도 → 재사용 오탐 → 세션 폐기 방지)
            localStorage.setItem('tokenSync', JSON.stringify({
                access: access,
                refresh: res.data.refreshToken || '',
                ts: Date.now()
            }))
            localStorage.removeItem('tokenSync')

            return access
        })
        .catch(err => {
            const st = err.response && err.response.status
            if (st >= 400 && st < 500) {
                forceLogout() // 4xx = 서버가 명시적으로 거부(만료/무효) → 이 탭 정리
            }
            // 5xx(배포·게이트웨이 순단)·네트워크 단절은 일시 장애 → 로그아웃하지 않음
            throw err
        })
}

// access JWT 의 exp 를 디코드해 만료 임박 여부 판단
export function isAccessExpiring (token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (!payload.exp) return false
        return payload.exp - Math.floor(Date.now() / 1000) <= 10 // 10초 이하 남으면 갱신
    } catch (e) {
        return false
    }
}

// refresh JWT 만료 여부 — 서버 호출 없이 로컬(exp 디코드)에서 판정 (복귀/부팅 시 stale 세션 감지용)
export function isRefreshExpired (refresh) {
    try {
        const payload = JSON.parse(atob(refresh.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (!payload.exp) return true
        return payload.exp + 300 <= Math.floor(Date.now() / 1000) // 기기 시계 오차 5분 여유 (오판 시 창 전환마다 튕김 방지)
    } catch (e) {
        return true // 디코드 불가 = 비정상 토큰 → 만료 취급
    }
}

// refresh single-flight — 선제(request)·반응(401 큐) 공용. 탭 내 이중 /refresh(회전 경쟁) 방지
let refreshInFlight = null
export function ensureFreshAccess () {
    if (!refreshInFlight) {
        refreshInFlight = performRefresh().finally(() => { refreshInFlight = null })
    }
    return refreshInFlight
}

axios.createInstance = function (instanceConfig) {
    const instance = axios.create(instanceConfig)

    instance.defaults.timeout = 600 * 1000

    //공통 헤더, sessionStorage에서 accesstoken 등 관리예정
    instance.interceptors.request.use(async function (config) {
        // console.time('url:' + config.url)
        let token = sessionStorage.getItem('access_token')
        const refreshToken = sessionStorage.getItem('refresh_token')

        // 선제적 refresh: 만료 임박 토큰이면 보내기 전에 미리 재발급 (동시 다발은 single-flight로 1회)
        // 최근 30초 내 refresh 했으면 skip — 기기 시계가 크게 틀어진 경우의 연속 refresh(회전 폭풍) 방지
        if (token && refreshToken && isAccessExpiring(token) && Date.now() - lastRefreshTs > 30000) {
            try {
                token = await ensureFreshAccess()
            } catch (e) {
                // refresh 실패 시 기존 토큰으로 진행 → 401 폴백 처리
                token = sessionStorage.getItem('access_token')
            }
        }

        if (token) {
            config.headers.jwt = token

            // 표준 JWT 방식 (Authorization: Bearer 토큰)
            config.headers.Authorization = 'Bearer ' + token
        }
        let serviceLang = localStorage.getItem('service_lang')
        if (serviceLang) {
            config.headers['service-lang'] = serviceLang
        }

        let serviceCtrCd = localStorage.getItem('service_ctrcd')
        if (serviceCtrCd) {
            config.headers['service-ctrcd'] = serviceCtrCd
        }

        if (location.host.includes('preview')) {
            config.baseURL = config.baseURL.replace('api.', 'api-preview.')
        }
        config.baseURL = toServiceDomain(config.baseURL)

        const selectedProfile = store.getters['auth/selectedProfile']
        if (location.href.indexOf('common/notice?') < 0) {
          config.headers['service-path'] = location.hash
        }

        //우선 프로필정보가 없는경우 헤더에 넣지 않도록 수정
        if (selectedProfile !== undefined && selectedProfile !== null) {
            config.headers['selected-profile'] = (JSON.stringify({
                cstNo: selectedProfile.cstNo,
                picNo: selectedProfile.picNo
            }))
        }

        // if (config.spinner ?? true) store.commit('startSpinner')
        // return config
        // 1) 호출자가 spinner: false 안 박았고
        // 2) 현재 $withSpinner 스코프 안이 아닐 때만 카운터 증가
        const shouldSpin = (config.spinner ?? true) && !isSpinnerSuppressed()
        config._spinnerHandled = shouldSpin // request 시점 결정을 config 에 박아 response/error 페어링 보장
        if (shouldSpin) store.commit('startSpinner')
        return config
    }, function (error) {
        // Do something with request error
        store.commit('endSpinner')
        return Promise.reject(error)
    })

    // Add a response interceptor
    instance.interceptors.response.use(function (response) {
        console.timeEnd('url:' + response.config.url)
        // if (response.config.spinner ?? true) store.commit('endSpinner')
        // request 시점에 박아둔 결정을 그대로 따라감
        if (response.config._spinnerHandled) store.commit('endSpinner')
        return response
    }, function (error, b) {
        // store.commit('endSpinner')
        if (error.config?._spinnerHandled !== false) {
          store.commit('endSpinner')
        }
        const code = error.response?.status
        const config = error.config

        // access 만료(401) → refresh 진행 중이면 큐 대기, 완료 후 원요청 재시도
        if (code === 401 && config && !config._retried && sessionStorage.getItem('refresh_token')) {
            config._retried = true

            const retry = new Promise((resolve, reject) => {
                // refresh 완료 후 호출됨 (access=null 이면 refresh 실패)
                refreshSubscribers.push(access => {
                    if (!access) {
                        reject(error) // refresh 실패 → 이 요청도 실패(세션 종료는 아래 refresh catch가 일괄 처리)
                        return
                    }
                    config.headers.jwt = access
                    config.headers.Authorization = 'Bearer ' + access
                    // 재시도 자체가 실패해도 로그아웃시키지 않음(그 요청만 실패 처리)
                    resolve(instance.request(config))
                })
            })

            if (!isRefreshing) {
                isRefreshing = true
                ensureFreshAccess() // 선제 refresh 와 같은 single-flight 공유 (탭 내 이중 /refresh 방지)
                    .then(access => {
                        isRefreshing = false
                        onRefreshDone(access) // 대기 중인 모든 요청 일괄 재시도
                    })
                    .catch(() => {
                        isRefreshing = false
                        onRefreshDone(null) // 대기자 전부 실패 통지(재시도 안 함)
                        // 로그아웃 여부는 performRefresh 내부(forceLogout: 4xx 거부 시)가 이미 판정.
                        // 네트워크 순단 등으로 여기 온 경우까지 로그아웃하지 않는다.
                    })
            }

            return retry
        }

        // 재시도 후에도 401 = 방금 refresh 가 성공했는데도 401 → 세션 문제가 아니라
        // 권한/인가성 401(백엔드가 Role 미달·회원조회 실패에도 401 사용) → 그 요청만 실패, 로그아웃 금지
        if (code === 401 && config && config._retried) {
            return Promise.reject(error)
        }

        // refresh 토큰 자체가 없는 401(비로그인/스토리지 유실) → 이 탭만 정리 후 메인
        if (code === 401) {
            store.dispatch('auth/delLoginInfo', { quiet: true }).then(() => {
                location.replace('/')
            })
        }

        // 요청 정의에 errorAlert: true 를 지정한 API만 공통 오류 알림 (opt-in - 이중 알림/백그라운드 호출 오염 방지)
        // 시스템성 실패만 대상: 백엔드 시스템 오류(respCode C9999) / 5xx(게이트웨이 포함) / 응답 없음(네트워크 단절·타임아웃)
        // commonUtils는 api client를 거쳐 이 모듈을 되참조(순환 참조)하므로 top-level import 대신 사용 시점에 동적 로드
        const respCode = error.response?.headers?.respcode
        if (error.config?.errorAlert && (respCode === 'C9999' || code >= 500 || !error.response)) {
            Promise.all([import('../plugins/commonUtils'), import('../i18n')]).then(([commonUtils, i18n]) => {
                commonUtils.default.asyncAlertMessage({ message: i18n.default.t('msg.FARE010T010.023') }) // 처리 중 오류가 발생하였습니다. 시스템 관리자에게 문의해 주세요.
            })
        }

        return Promise.reject(error)
    })

    return instance
}

export default axios
